import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import "@/App.css";
import { SCENARIOS } from "@/data/scenarios";
import { REQUESTS_BY_SCENARIO } from "@/data/requests";
import { TIMELINE_STEPS, TIMELINE_DURATION_MS } from "@/data/timeline";
import { bloomRoute, katlasGovern, buildOutcome } from "@/lib/engine";
import { Header } from "@/components/Header";
import { Pipeline } from "@/components/Pipeline";
import { ScenarioPanel } from "@/components/ScenarioPanel";
import { RequestPanel } from "@/components/RequestPanel";
import { BloomPanel } from "@/components/BloomPanel";
import { KatlasPanel } from "@/components/KatlasPanel";
import { OutcomePanel } from "@/components/OutcomePanel";
import { ReceiptDrawer } from "@/components/ReceiptDrawer";
import { GuidedTour, TOUR_STEPS } from "@/components/GuidedTour";
import { CARStrip } from "@/components/CARStrip";
import { GovernedAction } from "@/components/GovernedAction";
import { PublicReceipt } from "@/components/PublicReceipt";

const DEFAULT_SCENARIO = "urban-flooding";

// ---------- Top-level routes ----------

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/r/:hash" element={<PublicReceipt />} />
        <Route path="/*" element={<MainShell />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;

// ---------- Main app shell with tab switcher ----------

function MainShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const view = location.pathname.startsWith("/walkthrough") ? "walkthrough" : "governed";

  return (
    <div className="App h-screen w-full flex flex-col bg-[#F9F9F8] overflow-hidden">
      <TopBar
        view={view}
        onChange={(v) => navigate(v === "walkthrough" ? "/walkthrough" : "/")}
      />
      <CARStrip />
      {view === "governed" ? <GovernedAction /> : <ConceptWalkthrough />}
    </div>
  );
}

function TopBar({ view, onChange }) {
  return (
    <div className="h-12 px-6 border-b border-stone-200 bg-white flex items-center justify-between shrink-0">
      <Link to="/" className="flex items-center gap-2.5">
        <div className="w-7 h-7 rounded-sm bg-stone-900 flex items-center justify-center">
          <span className="font-serif italic text-white text-base leading-none">Ai</span>
        </div>
        <div className="flex flex-col leading-tight">
          <span className="text-[11px] uppercase tracking-[0.18em] text-stone-500 font-medium">
            AICierge
          </span>
          <span className="text-xs text-stone-900 font-medium">
            Governed AI operations <span className="text-stone-300">·</span> policy gates{" "}
            <span className="text-stone-300">·</span> signed receipts
          </span>
        </div>
      </Link>

      <nav className="flex items-center gap-1 bg-stone-100 rounded-sm p-0.5" data-testid="view-tabs">
        <TabBtn
          active={view === "governed"}
          onClick={() => onChange("governed")}
          label="Governed action"
          testid="tab-governed"
        />
        <TabBtn
          active={view === "walkthrough"}
          onClick={() => onChange("walkthrough")}
          label="Concept walkthrough"
          testid="tab-walkthrough"
        />
      </nav>
    </div>
  );
}

const TabBtn = ({ active, onClick, label, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-colors ${
      active ? "bg-white text-stone-900 shadow-sm" : "text-stone-500 hover:text-stone-700"
    }`}
  >
    {label}
  </button>
);

// ---------- Concept walkthrough (was the previous sandbox) ----------

function ConceptWalkthrough() {
  const [scenarioId, setScenarioId] = useState(DEFAULT_SCENARIO);
  const [severity, setSeverity] = useState(2);
  const [compareMode, setCompareMode] = useState("auto");
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [statuses, setStatuses] = useState({});
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Replay
  const [isReplaying, setIsReplaying] = useState(false);
  const [replayProgress, setReplayProgress] = useState(0);
  const [replayLabel, setReplayLabel] = useState(null);
  const replayTimers = useRef([]);

  // Guided walkthrough
  const [tourOpen, setTourOpen] = useState(false);
  const [tourStep, setTourStep] = useState(0);

  const scenario = useMemo(() => SCENARIOS.find((s) => s.id === scenarioId), [scenarioId]);
  const requests = useMemo(() => REQUESTS_BY_SCENARIO[scenarioId] || [], [scenarioId]);

  useEffect(() => {
    if (requests.length > 0) setSelectedRequestId(requests[0].id);
  }, [scenarioId]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedRequest = useMemo(
    () => requests.find((r) => r.id === selectedRequestId) || requests[0],
    [requests, selectedRequestId]
  );

  const bloom = useMemo(() => {
    if (!selectedRequest) return null;
    return bloomRoute({ request: selectedRequest, severity, mode: compareMode });
  }, [selectedRequest, severity, compareMode]);

  const katlas = useMemo(() => {
    if (!selectedRequest) return null;
    return katlasGovern({ request: selectedRequest, severity });
  }, [selectedRequest, severity]);

  const outcome = useMemo(() => {
    if (!selectedRequest || !bloom || !katlas) return null;
    return buildOutcome({ request: selectedRequest, bloom, katlas });
  }, [selectedRequest, bloom, katlas]);

  useEffect(() => {
    if (!outcome || !selectedRequest) return;
    setReceipts((prev) => {
      const filtered = prev.filter((r) => r.id !== outcome.receipt.id);
      return [{ ...outcome.receipt }, ...filtered].slice(0, 50);
    });
    setStatuses((prev) => ({ ...prev, [selectedRequest.id]: katlas.status }));
  }, [outcome?.receipt.id, selectedRequest?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearReplayTimers = () => {
    replayTimers.current.forEach((t) => window.clearTimeout(t));
    replayTimers.current = [];
  };

  const stopReplay = useCallback(() => {
    clearReplayTimers();
    setIsReplaying(false);
    setReplayProgress(0);
    setReplayLabel(null);
  }, []);

  const startReplay = useCallback(() => {
    clearReplayTimers();
    setTourOpen(false);
    setIsReplaying(true);
    setReplayProgress(0);
    TIMELINE_STEPS.forEach((step) => {
      const t = window.setTimeout(() => {
        setSeverity(step.severity);
        setCompareMode(step.mode || "auto");
        setReplayLabel(step.label);
        setReplayProgress(step.at_ms / TIMELINE_DURATION_MS);
        setSelectedRequestId((prev) => {
          const target = (REQUESTS_BY_SCENARIO[scenarioId] || []).find((r) => r.flow === step.flow);
          return target ? target.id : prev;
        });
        if (step.end) window.setTimeout(() => stopReplay(), 800);
      }, step.at_ms);
      replayTimers.current.push(t);
    });
  }, [scenarioId, stopReplay]);

  const toggleReplay = useCallback(() => {
    if (isReplaying) stopReplay();
    else startReplay();
  }, [isReplaying, startReplay, stopReplay]);

  useEffect(() => {
    if (isReplaying) stopReplay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarioId]);

  useEffect(() => () => clearReplayTimers(), []);

  const startTour = useCallback(() => {
    stopReplay();
    setTourOpen(true);
    setTourStep(0);
  }, [stopReplay]);

  const closeTour = useCallback(() => setTourOpen(false), []);
  const tourNext = useCallback(() => {
    setTourStep((s) => {
      const next = Math.min(TOUR_STEPS.length - 1, s + 1);
      if (TOUR_STEPS[next].id === "katlas") setSeverity((sv) => Math.max(sv, 3));
      return next;
    });
  }, []);
  const tourPrev = useCallback(() => setTourStep((s) => Math.max(0, s - 1)), []);
  const tourJump = useCallback((i) => setTourStep(i), []);

  const handleReset = useCallback(() => {
    stopReplay();
    setTourOpen(false);
    setScenarioId(DEFAULT_SCENARIO);
    setSeverity(2);
    setCompareMode("auto");
    setReceipts([]);
    setStatuses({});
    setDrawerOpen(false);
    if (REQUESTS_BY_SCENARIO[DEFAULT_SCENARIO]) {
      setSelectedRequestId(REQUESTS_BY_SCENARIO[DEFAULT_SCENARIO][0].id);
    }
  }, [stopReplay]);

  const handleRunFlow = useCallback(
    (flow) => {
      stopReplay();
      const target = requests.find((r) => r.flow === flow);
      if (!target) return;
      setSelectedRequestId(target.id);
      setCompareMode("auto");
      if (flow === "low") setSeverity((s) => Math.min(s, 2));
      if (flow === "escalating") setSeverity(3);
      if (flow === "sensitive") setSeverity(3);
    },
    [requests, stopReplay]
  );

  const activeStage = useMemo(() => {
    if (tourOpen) return TOUR_STEPS[tourStep].id;
    if (!selectedRequest) return "rwi";
    if (!katlas) return "request";
    if (katlas.status === "held" || katlas.status === "denied") return "katlas";
    if (outcome) return "outcome";
    return "bloom";
  }, [tourOpen, tourStep, selectedRequest, katlas, outcome]);

  return (
    <>
      <Header
        scenarioId={scenarioId}
        setScenarioId={setScenarioId}
        severity={severity}
        setSeverity={setSeverity}
        onReset={handleReset}
        onOpenReceipts={() => setDrawerOpen(true)}
        receiptCount={receipts.length}
        onRunFlow={handleRunFlow}
        isReplaying={isReplaying}
        onToggleReplay={toggleReplay}
        replayProgress={replayProgress}
        onStartTour={startTour}
      />
      <Pipeline activeStage={activeStage} />
      {isReplaying && replayLabel && (
        <div
          data-testid="replay-banner"
          className="px-6 py-1.5 bg-stone-900 text-white text-xs flex items-center gap-3"
        >
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/60">
            Timeline replay
          </span>
          <span className="text-white/90">{replayLabel}</span>
          <div className="ml-auto w-40 h-0.5 bg-white/20 rounded-full overflow-hidden">
            <div
              className="h-full bg-white transition-all duration-300"
              style={{ width: `${Math.round(replayProgress * 100)}%` }}
            />
          </div>
        </div>
      )}
      <main className="flex-1 flex overflow-x-auto overflow-y-hidden p-6 gap-4 bg-[#F9F9F8] scroll-soft">
        <ScenarioPanel scenario={scenario} severity={severity} />
        <RequestPanel
          requests={requests}
          selectedId={selectedRequest?.id}
          onSelect={setSelectedRequestId}
          statuses={statuses}
        />
        <BloomPanel bloom={bloom} compareMode={compareMode} setCompareMode={setCompareMode} />
        <KatlasPanel katlas={katlas} request={selectedRequest} />
        <OutcomePanel outcome={outcome} />
      </main>
      <footer className="px-6 py-2 border-t border-stone-200 bg-white text-[11px] text-stone-500 flex items-center justify-between shrink-0">
        <span>
          Synthetic data · concept walkthrough · no production systems or partner identifiers are exposed.
        </span>
        <span className="font-mono text-stone-400">AICierge · Governed AI operations</span>
      </footer>
      <ReceiptDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} receipts={receipts} />
      <GuidedTour
        open={tourOpen}
        step={tourStep}
        onPrev={tourPrev}
        onNext={tourNext}
        onClose={closeTour}
        onJump={tourJump}
      />
    </>
  );
}
